import { camelCase, set, get } from 'lodash';
import { sentenceCase, toLowerCaseFirstLetter } from './StringUtilities';
import { Nullable, NullableKeys, OptionalStringKeys } from './Types';

export type ValidationFailure = {
    propertyName: string;
    errorMessage: string;
    errorCode?: string;
};

export type ValidationData = ValidationFailure[];

export type DomainValidationError = {
    code: string;
    content?: ValidationData;
    message: string;
};

export type DomainErrorCodeLocalizer = (codes: ResourceFile, code: ValidationFailure | DomainValidationError) => string;

export const errorCodeLocalizer: DomainErrorCodeLocalizer = (codes: ResourceFile, error) => {
    if (isDomainValidationError(error)) {
        return (!!error.code && get(codes, error.code, null)) || error.message;
    }

    return (!!error.errorCode && get(codes, error.errorCode, null)) || error.errorMessage;
};

export function isDomainValidationError(errorPayload: unknown): errorPayload is DomainValidationError {
    const typedPayload = errorPayload as DomainValidationError;
    return typedPayload.code !== undefined && typedPayload.content !== undefined;
}

export type ResourceFile = {
    [key: string]: string;
};

export function getFirstErrorMessageFromApiError(
    result: unknown,
    codes?: ResourceFile,
    defaultMessage?: string
): string {
    if (isDomainValidationError(result)) {
        if (result.content && result.content.length > 0) {
            if (codes) return errorCodeLocalizer(codes, result.content[0]);

            return result.content[0].errorMessage;
        }

        if (codes) return errorCodeLocalizer(codes, result);
    }

    let message: Nullable<string> = null;

    if (result instanceof Error) {
        message = result.message;
    }

    return message || defaultMessage || 'Api Error';
}

export function toNullableKeys<T extends object>(form: T): NullableKeys<T> {
    const returnObject: NullableKeys<T> = {} as NullableKeys<T>;
    Object.keys(form).forEach(key => {
        returnObject[key as keyof T] = null;
    });

    return returnObject;
}

export declare type ApiFormErrors<Values> = {
    [K in keyof Values]?: Values[K] extends object ? ApiFormErrors<Values[K]> : string;
};

/**
 * Converts our convention based API validation error result into a usable error block and global errors list.
 */
export function convertValidationResultToErrorsObjectV2<T extends object>(
    results?: ValidationData,
    codes?: ResourceFile,
    propertyNameConverter?: (path: string) => string
): {
    fieldErrors: ApiFormErrors<T>;
    hasApiErrors: boolean;
} {
    const fieldErrors: ApiFormErrors<T> = {} as ApiFormErrors<T>;
    let hasApiErrors = false;
    if (!results) {
        return {
            fieldErrors: {},
            hasApiErrors: false
        };
    }
    if (results.length > 0) {
        hasApiErrors = true;
        results.forEach(it => {
            const propertyName = propertyNameConverter ? propertyNameConverter(it.propertyName) : it.propertyName;
            const lowerCasePropertyName = propertyName.split('.').map(toLowerCaseFirstLetter).join('.');
            set(fieldErrors, lowerCasePropertyName, codes ? errorCodeLocalizer(codes, it) : it.errorMessage);
        });
    }
    return {
        fieldErrors,
        hasApiErrors
    };
}

/**
 * Converts our convention based API validation error result into a usable error block and global errors list.
 */
export function convertValidationResultToErrorsObject<T extends object>({
    result,
    formStructure
}: {
    result?: ValidationData;
    formStructure: NullableKeys<T>;
}): {
    formErrors: OptionalStringKeys<T>; // Form aligned error messages, this can but pushed directly into the forms error object.
    hasFormErrors: boolean; // Convenience boolean to quickly identify if any form field has errored.
    apiErrors: string[]; // A unique list of all other errors that couldn't be aligned to a form field.
    hasApiErrors: boolean; // Convenience boolean to quickly identify if a non-form field error has returned.
} {
    if (!result) {
        return {
            formErrors: {},
            hasFormErrors: false,
            apiErrors: [],
            hasApiErrors: false
        };
    }
    return result.reduce(
        (errorObject: any, validationError) => {
            const { propertyName, errorMessage } = validationError;

            const adjustPropertyName = camelCase(propertyName);

            if (formStructure.hasOwnProperty(adjustPropertyName)) {
                errorObject.hasFormErrors = true;
                if (!errorObject.formErrors.hasOwnProperty(adjustPropertyName)) {
                    errorObject.formErrors[adjustPropertyName] = errorMessage;
                }
            } else {
                errorObject.hasApiErrors = true;
                if (!errorObject.apiErrors.some((error: string) => error === errorMessage)) {
                    let message = errorMessage;
                    if (message === 'This field is required')
                        message = `${errorMessage} (${sentenceCase(adjustPropertyName)})`;

                    (errorObject.apiErrors as string[]).push(message);
                }
            }

            return errorObject;
        },
        {
            formErrors: {},
            hasFormErrors: false,
            apiErrors: [],
            hasApiErrors: false
        }
    );
}
